import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type { DeleteViewConfig, DeleteViewConfigVariables } from 'types/graphql';
import type { ViewConfigPayload } from './viewConfig.store';
import { FIND_VIEW_CONFIGURATION_PAGE, viewConfigurationPageVariables } from './viewConfigurationReads';

const CREATE_VIEW_CONFIG = gql`
  mutation CreateViewConfig($input: CreateViewConfigurationInput!) {
    createViewConfiguration(input: $input) {
      id
      name
      config
    }
  }
`;

const UPDATE_VIEW_CONFIG = gql`
  mutation UpdateViewConfig($id: Int!, $input: UpdateViewConfigurationInput!) {
    updateViewConfiguration(id: $id, input: $input) {
      id
    }
  }
`;

const DELETE_VIEW_CONFIG = gql`
  mutation DeleteViewConfig($id: Int!) {
    deleteViewConfiguration(id: $id) {
      id
    }
  }
`;

export type NewViewConfiguration = { name: string; config: ViewConfigPayload; page: string };
export type ViewConfigurationPatch = { config?: ViewConfigPayload; name?: string };

/** Saves a named view and refetches that page's first page before resolving, so the new view survives a remount. */
export function useCreateViewConfiguration() {
  // Ungenericised as before: the JSON-scalar payload has no generated type it fits (FU-VC-T).
  const [mutate, { loading, error }] = useMutation(CREATE_VIEW_CONFIG);
  const create = useCallback(
    (input: NewViewConfiguration) =>
      mutate({
        variables: { input },
        refetchQueries: [
          { query: FIND_VIEW_CONFIGURATION_PAGE, variables: viewConfigurationPageVariables(input.page) },
        ],
        awaitRefetchQueries: true,
      }),
    [mutate]
  );
  return { create, loading, error };
}

/** Re-saves and/or renames a view. An absent config and an empty name stay out of the input. */
export function useUpdateViewConfiguration() {
  const [mutate, { loading, error }] = useMutation(UPDATE_VIEW_CONFIG);
  const update = useCallback(
    ({ id, input }: { id: number; input: ViewConfigurationPatch }) => {
      const sent: ViewConfigurationPatch = {};
      if (input.config) sent.config = input.config;
      if (input.name) sent.name = input.name;
      return mutate({ variables: { id, input: sent } });
    },
    [mutate]
  );
  return { update, loading, error };
}

export function useDeleteViewConfiguration() {
  const [mutate, { loading, error }] = useMutation<DeleteViewConfig, DeleteViewConfigVariables>(DELETE_VIEW_CONFIG);
  const remove = useCallback((id: number) => mutate({ variables: { id } }), [mutate]);
  return { remove, loading, error };
}
